import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Cpu, Speaker, Gamepad2, Smartphone, Watch, 
  Cable, Wrench, Shield, Tag, MapPin, Trash2
} from 'lucide-react';
import type { DiscountPurchase, MembershipPlan } from './types';
import { DeleteRecordDialog } from './DeleteRecordDialog';

interface DiscountPurchasesHistoryProps {
  purchases: DiscountPurchase[];
  plan: MembershipPlan;
  limit?: number;
  currentUserId?: string;
  isAdmin?: boolean;
  onDelete?: (purchaseId: string, reason: string) => Promise<void>;
}

const categoryIcons: Record<string, React.ElementType> = {
  'Tecnología': Cpu,
  'Audio': Speaker,
  'Gaming': Gamepad2,
  'Estuches': Smartphone,
  'Relojes': Watch,
  'Cargadores': Cable,
  'Reparaciones': Wrench,
  'Vidrios': Shield,
};

const planStyles = {
  bronce: {
    icon: 'text-amber-500',
    tag: 'bg-amber-500/20 text-amber-400',
  },
  plata: {
    icon: 'text-slate-400',
    tag: 'bg-slate-500/20 text-slate-300',
  },
  oro: {
    icon: 'text-yellow-500',
    tag: 'bg-yellow-500/20 text-yellow-400',
  },
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const DiscountPurchasesHistory: React.FC<DiscountPurchasesHistoryProps> = ({
  purchases,
  plan,
  limit,
  currentUserId,
  isAdmin = false,
  onDelete,
}) => {
  const styles = planStyles[plan];
  const displayPurchases = limit ? purchases.slice(0, limit) : purchases;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<DiscountPurchase | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDeletePurchase = (_purchase: DiscountPurchase) => {
    if (!onDelete) return false;
    return isAdmin; // Solo admins pueden eliminar compras
  };

  const handleDeleteClick = (purchase: DiscountPurchase) => {
    setSelectedPurchase(purchase);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (reason: string) => {
    if (!selectedPurchase || !onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(selectedPurchase.id, reason);
      setDeleteDialogOpen(false);
      setSelectedPurchase(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (purchases.length === 0) {
    return (
      <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <ShoppingBag className="w-5 h-5 text-purple-400" />
          <span className="text-white font-medium">Historial de Compras con Descuento</span>
        </div>
        <p className="text-white/50 text-sm text-center py-6">
          No hay compras registradas aún
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-purple-400" />
          <span className="text-white font-medium">Historial de Compras con Descuento</span>
        </div>
        <span className="text-white/40 text-sm">{purchases.length} compra{purchases.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Purchases List */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto">
        {displayPurchases.map((purchase, index) => {
          const CategoryIcon = categoryIcons[purchase.category] || ShoppingBag;
          
          return (
            <motion.div
              key={purchase.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/5 rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors group"
            >
              <div className="flex items-start gap-3">
                {/* Category Icon */}
                <div className="bg-purple-500/20 rounded-lg p-2 flex-shrink-0">
                  <CategoryIcon className="w-5 h-5 text-purple-400" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{purchase.product_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${styles.tag}`}>
                          {purchase.category}
                        </span>
                        {purchase.quantity > 1 && (
                          <span className="text-white/50 text-xs">x{purchase.quantity}</span>
                        )}
                      </div>
                    </div>

                    {/* Savings & Delete */}
                    <div className="flex items-start gap-2 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-green-400 font-semibold">
                          -₡{purchase.savings.toLocaleString()}
                        </p>
                        <p className="text-white/40 text-xs flex items-center gap-1 justify-end">
                          <Tag className="w-3 h-3" />
                          {purchase.discount_percentage}% off
                        </p>
                      </div>
                      {canDeletePurchase(purchase) && (
                        <button
                          onClick={() => handleDeleteClick(purchase)}
                          className="p-1.5 rounded-lg bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/20 transition-all"
                          title="Eliminar compra"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Details row */}
                  <div className="flex items-center justify-between mt-2 text-xs text-white/40">
                    <div className="flex items-center gap-3">
                      <span>₡{purchase.original_price.toLocaleString()} → ₡{purchase.final_price.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {purchase.registrar_name && (
                        <span className="flex items-center gap-1">
                          por {purchase.registrar_name}
                          {purchase.registrar_ubicacion && (
                            <span className="flex items-center gap-0.5 text-blue-400/70">
                              <MapPin className="w-3 h-3" />
                              {purchase.registrar_ubicacion}
                            </span>
                          )}
                        </span>
                      )}
                      <span>{formatDate(purchase.created_at)}</span>
                    </div>
                  </div>

                  {/* Notes */}
                  {purchase.notes && (
                    <p className="text-white/50 text-xs mt-2 italic">"{purchase.notes}"</p>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Show more indicator */}
      {limit && purchases.length > limit && (
        <p className="text-center text-white/40 text-sm mt-4">
          +{purchases.length - limit} compras más
        </p>
      )}

      <DeleteRecordDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedPurchase(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Compra"
        description={selectedPurchase 
          ? `¿Eliminar la compra de "${selectedPurchase.product_name}"?`
          : ''
        }
        isDeleting={isDeleting}
      />
    </motion.div>
  );
};

export default DiscountPurchasesHistory;
